import { User } from 'firebase/auth';
import { Recipe } from '../types/mealPlan';
import { getPageCache, setPageCache } from './pageCache';
import { SUMMARY_VIEW_PARAM } from './recipeSummary';

/**
 * Shared session cache + fetcher for the recipe lists several surfaces load:
 *
 * - `cookbook` (`/api/recipes/cookbook`, saved recipes): the meal plan's
 *   swap/add drawer and the wizard picker's "Browse my cookbook".
 * - `archive` (`/api/recipes/archive`): the Recipe Archive page and the swap
 *   drawer's archive tab.
 *
 * Surfaces seed from the cached list (rendering instantly instead of showing
 * skeletons every time a drawer opens) and revalidate in the background via
 * `fetchRecipeList`, which dedupes concurrent requests for the same list and
 * writes the cache on success. Items are list summaries — see
 * `utils/recipeSummary.ts`.
 */
export type RecipeListKind = 'cookbook' | 'archive';

const ENDPOINTS: Record<RecipeListKind, string> = {
  cookbook: `/api/recipes/cookbook?${SUMMARY_VIEW_PARAM}`,
  archive: `/api/recipes/archive?${SUMMARY_VIEW_PARAM}`,
};

/** Cache key for a user's list; null until the uid is known. */
export const recipeListCacheKey = (
  kind: RecipeListKind,
  uid: string | null | undefined
): string | null =>
  uid ? (kind === 'cookbook' ? `cookbook-list:${uid}` : `recipe-archive:${uid}`) : null;

export const getCachedRecipeList = (
  kind: RecipeListKind,
  uid: string | null | undefined
): Recipe[] | undefined => getPageCache<Recipe[]>(recipeListCacheKey(kind, uid));

const inFlight = new Map<string, Promise<Recipe[]>>();

/**
 * Fetch a list, sharing an in-flight request for the same list and user.
 * Resolves with the fresh list (also written to the cache); rejects on a
 * failed request, leaving the cache untouched.
 */
export const fetchRecipeList = (user: User, kind: RecipeListKind): Promise<Recipe[]> => {
  const key = recipeListCacheKey(kind, user.uid)!;
  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = (async () => {
    const token = await user.getIdToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}${ENDPOINTS[kind]}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${kind} recipes`);
    }
    const data = await response.json();
    const recipes: Recipe[] = data.recipes || [];
    setPageCache(key, recipes);
    return recipes;
  })().finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, request);
  return request;
};
